import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

const STORAGE_KEY_PREFIX = "reviewboost-theme";
const ACCENT_STORAGE_KEY_PREFIX = "reviewboost-accent";
export type Theme = "light" | "dark" | "system";

function storageKey(prefix: string, userId: string | null): string {
  return `${prefix}-${userId ?? "anonymous"}`;
}

function readStored(userId: string | null): Theme {
  if (typeof window === "undefined") return "light";
  try {
    const key = storageKey(STORAGE_KEY_PREFIX, userId);
    const v = localStorage.getItem(key);
    if (v === "dark" || v === "light" || v === "system") return v;
  } catch {
    /* ignore */
  }
  return "light";
}

function readStoredAccent(userId: string | null): string | null {
  if (typeof window === "undefined") return null;
  try {
    const key = storageKey(ACCENT_STORAGE_KEY_PREFIX, userId);
    const v = localStorage.getItem(key);
    if (v && /^#[0-9A-Fa-f]{6}$/.test(v)) return v;
  } catch {
    /* ignore */
  }
  return null;
}

/** Convert hex to HSL string for CSS (e.g. "120 73% 75%"). */
function hexToHsl(hex: string): string {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) / 255;
  const g = ((n >> 8) & 0xff) / 255;
  const b = (n & 0xff) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  const s = l > 0.5 ? (max - min) / (2 - max - min) : (max - min) / (max + min);
  const H = Math.round(h * 360);
  const S = Math.round(s * 100);
  const L = Math.round(l * 100);
  return `${H} ${S}% ${L}%`;
}

/** Return black or white foreground for contrast on the given hex background. */
function getContrastForeground(hex: string): string {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) / 255;
  const g = ((n >> 8) & 0xff) / 255;
  const b = (n & 0xff) / 255;
  const l = 0.299 * r + 0.587 * g + 0.114 * b;
  return l > 0.5 ? "0 0% 0%" : "0 0% 100%";
}

const ACCENT_VARS = [
  "accent",
  "accent-foreground",
  "primary",
  "primary-foreground",
  "ring",
  "sidebar-primary",
  "sidebar-primary-foreground",
  "sidebar-ring",
] as const;

function applyAccent(hex: string | null) {
  const root = document.documentElement;
  if (!hex) {
    ACCENT_VARS.forEach((name) => root.style.removeProperty(`--${name}`));
    return;
  }
  const hsl = hexToHsl(hex);
  const fg = getContrastForeground(hex);
  root.style.setProperty("--accent", hsl);
  root.style.setProperty("--accent-foreground", fg);
  root.style.setProperty("--primary", hsl);
  root.style.setProperty("--primary-foreground", fg);
  root.style.setProperty("--ring", hsl);
  root.style.setProperty("--sidebar-primary", hsl);
  root.style.setProperty("--sidebar-primary-foreground", fg);
  root.style.setProperty("--sidebar-ring", hsl);
}

function getSystemTheme(): "light" | "dark" {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function resolveTheme(preference: Theme): "light" | "dark" {
  if (preference === "system") return getSystemTheme();
  return preference;
}

function applyTheme(resolved: "light" | "dark") {
  const root = document.documentElement;
  if (resolved === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
}

const ThemeContext = createContext<{
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: "light" | "dark";
  toggleTheme: () => void;
  accentColor: string | null;
  setAccentColor: (hex: string | null) => void;
}>({
  theme: "light",
  setTheme: () => {},
  resolvedTheme: "light",
  toggleTheme: () => {},
  accentColor: null,
  setAccentColor: () => {},
});

export function useTheme() {
  return useContext(ThemeContext);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    const setUserFromSession = (session: { user: { id: string } } | null) => {
      setUserId(session?.user?.id ?? null);
    };
    supabase.auth.getSession().then(({ data: { session } }) => setUserFromSession(session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserFromSession(session);
    });
    return () => subscription.unsubscribe();
  }, []);

  const [theme, setThemeState] = useState<Theme>(() => readStored(null));
  const [resolvedTheme, setResolvedTheme] = useState<"light" | "dark">(() => resolveTheme(readStored(null)));
  const [accentColor, setAccentColorState] = useState<string | null>(() => readStoredAccent(null));

  useEffect(() => {
    if (userId === null) {
      const t = readStored(null);
      const a = readStoredAccent(null);
      setThemeState(t);
      setResolvedTheme(resolveTheme(t));
      setAccentColorState(a);
      applyTheme(resolveTheme(t));
      applyAccent(a);
      return;
    }
    let cancelled = false;
    supabase
      .from("user_preferences")
      .select("theme, accent_color")
      .eq("user_id", userId)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        const dbTheme = data?.theme;
        const dbAccent = data?.accent_color ?? null;
        const t: Theme =
          dbTheme === "dark" || dbTheme === "light" || dbTheme === "system"
            ? dbTheme
            : readStored(userId);
        const a =
          dbAccent && /^#[0-9A-Fa-f]{6}$/.test(dbAccent)
            ? dbAccent
            : readStoredAccent(userId);
        setThemeState(t);
        setResolvedTheme(resolveTheme(t));
        setAccentColorState(a);
        applyTheme(resolveTheme(t));
        applyAccent(a);
        try {
          localStorage.setItem(storageKey(STORAGE_KEY_PREFIX, userId), t);
          if (a) localStorage.setItem(storageKey(ACCENT_STORAGE_KEY_PREFIX, userId), a);
          else localStorage.removeItem(storageKey(ACCENT_STORAGE_KEY_PREFIX, userId));
        } catch {
          /* ignore */
        }
      })
      .catch(() => {
        if (cancelled) return;
        const t = readStored(userId);
        const a = readStoredAccent(userId);
        setThemeState(t);
        setResolvedTheme(resolveTheme(t));
        setAccentColorState(a);
        applyTheme(resolveTheme(t));
        applyAccent(a);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    const resolved = resolveTheme(theme);
    setResolvedTheme(resolved);
    applyTheme(resolved);
    try {
      localStorage.setItem(storageKey(STORAGE_KEY_PREFIX, userId), theme);
    } catch {
      /* ignore */
    }
  }, [theme, userId]);

  useEffect(() => {
    applyAccent(accentColor);
  }, [accentColor]);

  useEffect(() => {
    try {
      if (accentColor) localStorage.setItem(storageKey(ACCENT_STORAGE_KEY_PREFIX, userId), accentColor);
      else localStorage.removeItem(storageKey(ACCENT_STORAGE_KEY_PREFIX, userId));
    } catch {
      /* ignore */
    }
  }, [accentColor, userId]);

  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  const syncToServer = useCallback((t: Theme, a: string | null) => {
    const uid = userIdRef.current;
    if (!uid) return;
    supabase
      .from("user_preferences")
      .upsert(
        {
          user_id: uid,
          theme: t,
          accent_color: a ?? "#16a34a",
          updated_at: new Date().toISOString(),
        },
        { onConflict: "user_id" }
      )
      .then(({ error }) => {
        if (error) console.warn("ThemeContext: failed to sync appearance to server", error.message);
      });
  }, []);

  useEffect(() => {
    if (theme !== "system") return;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      const resolved = getSystemTheme();
      setResolvedTheme(resolved);
      applyTheme(resolved);
    };
    handler();
    media.addEventListener("change", handler);
    return () => media.removeEventListener("change", handler);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    syncToServer(next, accentColor);
  }, [accentColor, syncToServer]);

  const toggleTheme = useCallback(() => {
    setThemeState((t) => {
      const next = t === "light" ? "dark" : t === "dark" ? "light" : "light";
      syncToServer(next, accentColor);
      return next;
    });
  }, [accentColor, syncToServer]);

  const setAccentColor = useCallback((hex: string | null) => {
    setAccentColorState(hex);
    syncToServer(theme, hex);
  }, [theme, syncToServer]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, resolvedTheme, toggleTheme, accentColor, setAccentColor }}>
      {children}
    </ThemeContext.Provider>
  );
}
